import {
  getDownloadURL,
  getMetadata,
  listAll,
  ref,
  uploadBytes,
} from "firebase/storage";

import { storage } from "@/firebase";

function requestFolderPath(venueId, requestId) {
  return `venues/${venueId}/artwork_requests/${requestId}`;
}

function safeFileName(value) {
  const cleaned = String(value || "artwork")
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return cleaned || "artwork";
}

function uniqueArtworkPath(venueId, requestId, file) {
  const timestamp = Date.now();

  const randomPart = Math.random()
    .toString(36)
    .slice(2, 10);

  const fileName = safeFileName(file?.name);

  return `${requestFolderPath(
    venueId,
    requestId
  )}/${timestamp}-${randomPart}-${fileName}`;
}

function metadataDateValue(metadata) {
  const value =
    metadata?.updated ||
    metadata?.timeCreated ||
    "";

  const timestamp = Date.parse(value);

  return Number.isNaN(timestamp)
    ? 0
    : timestamp;
}

export async function uploadArtworkFile({
  venueId,
  requestId,
  file,
}) {
  if (!venueId || !requestId || !file) {
    throw new Error(
      "Venue, request and artwork file are required."
    );
  }

  /*
   * IMPORTANT:
   * Every upload gets its own Storage object.
   *
   * We deliberately keep the object directly inside:
   *
   * venues/{venueId}/artwork_requests/{requestId}/
   *
   * so the existing Firebase Storage rule still matches.
   */
  const storagePath = uniqueArtworkPath(
    venueId,
    requestId,
    file
  );

  const artworkRef = ref(
    storage,
    storagePath
  );

  await uploadBytes(
    artworkRef,
    file,
    {
      contentType:
        file.type ||
        "application/octet-stream",

      customMetadata: {
        venueId,
        requestId,
        originalFileName:
          file.name || "Artwork file",
      },
    }
  );

  const downloadUrl =
    await getDownloadURL(artworkRef);

  return {
    storagePath,
    downloadUrl,
    fileName:
      file.name || "Artwork file",
    contentType:
      file.type ||
      "application/octet-stream",
    updatedAt:
      new Date().toISOString(),
  };
}

/*
 * Legacy artwork recovery.
 *
 * This remains in place so requests created before multiple
 * artwork versions were introduced still work.
 */
export async function recoverStoredArtwork({
  venueId,
  requestId,
  artwork = null,
}) {
  if (!venueId || !requestId) {
    return null;
  }

  const existingArtwork =
    artwork || {};

  if (existingArtwork.downloadUrl) {
    return existingArtwork;
  }

  /*
   * If Firestore does not contain a Storage path there is no
   * reliable indication that an artwork file existed.
   */
  if (!existingArtwork.storagePath) {
    return null;
  }

  const folderPath =
    requestFolderPath(
      venueId,
      requestId
    );

  const expectedPath =
    existingArtwork.storagePath;

  let storedRef =
    ref(storage, expectedPath);

  let metadata = null;

  try {
    metadata =
      await getMetadata(storedRef);
  } catch (expectedPathError) {
    try {
      const folderResult =
        await listAll(
          ref(storage, folderPath)
        );

      if (!folderResult.items.length) {
        return null;
      }

      const storedItems =
        await Promise.all(
          folderResult.items.map(
            async (itemRef) => ({
              itemRef,

              metadata:
                await getMetadata(
                  itemRef
                ),
            })
          )
        );

      storedItems.sort(
        (first, second) =>
          metadataDateValue(
            second.metadata
          ) -
          metadataDateValue(
            first.metadata
          )
      );

      storedRef =
        storedItems[0].itemRef;

      metadata =
        storedItems[0].metadata;
    } catch (folderError) {
      const expectedCode =
        String(
          expectedPathError?.code ||
          ""
        );

      const folderCode =
        String(
          folderError?.code ||
          ""
        );

      if (
        expectedCode.includes(
          "object-not-found"
        ) &&
        (
          folderCode.includes(
            "object-not-found"
          ) ||
          !folderCode
        )
      ) {
        return null;
      }

      throw folderError;
    }
  }

  const downloadUrl =
    await getDownloadURL(storedRef);

  return {
    storagePath:
      storedRef.fullPath,

    downloadUrl,

    fileName:
      metadata?.customMetadata
        ?.originalFileName ||
      existingArtwork.fileName ||
      metadata?.name ||
      "Artwork file",

    contentType:
      metadata?.contentType ||
      existingArtwork.contentType ||
      "image/jpeg",

    updatedAt:
      metadata?.updated ||
      existingArtwork.updatedAt ||
      new Date().toISOString(),
  };
}